<script setup lang="ts">
import {onBeforeUnmount, onMounted} from 'vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';

/**
 * A confirmation with nothing to do about it (IPD-3328), drawn as the design system's Toast: the
 * message alone, taking itself down. Anything the reader could act on belongs in a snackbar.
 *
 * Only what is seen lives here; the holder's permanent live region announces the message.
 */
const props = defineProps<{
  message: string;
  durationMs: number;
}>();

const emit = defineEmits<{
  /** Its time ran out. */
  close: [];
}>();

// No hold on hover or focus, unlike the snackbar: a toast takes no pointer and holds no control,
// so there is nothing for the reader to be on their way to.
let timer: number | null = null;

onMounted(() => {
  timer = window.setTimeout(() => {
    emit('close');
  }, props.durationMs);
});

onBeforeUnmount(() => {
  if (timer !== null) {
    window.clearTimeout(timer);
  }
});
</script>

<template>
  <div class="balm-toast" aria-hidden="true">
    <span class="bt-icon">
      <svg-illustration name="sign-up/check-circle.svg"/>
    </span>
    <span class="bt-text">{{ message }}</span>
  </div>
</template>

<style scoped lang="scss">
.balm-toast {
  display: flex;
  align-items: flex-start;
  // The design system's tokens under the same names, as in the snackbar. Its short toast: the right
  // side a step roomier than the side the icon sits on.
  gap: var(--Spacing-spacing-2);
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-4) var(--Spacing-spacing-3) var(--Spacing-spacing-3);
  border-radius: var(--border-radius);
  // The same inverse feedback surface as the snackbar.
  background: var(--Neutral-UI-neutral-ui-100);
  color: var(--Neutral-UI-neutral-ui-0);
  // It never blocks the page beneath it.
  pointer-events: none;

  // 14px at every width, as in the snackbar.
  @include font-body;
  line-height: 1.4;

  @include screen-lg {
    @include font-body-sm;
    line-height: 1.4;
  }
}

.bt-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: 1.4em;
  color: var(--Functional-wl-completed-surface);

  :deep(svg) {
    width: 16px;
    height: 16px;
    // The same optical lift as the snackbar's.
    transform: translateY(-1px);
  }
}

.bt-text {
  min-width: 0;
  overflow-wrap: break-word;
}
</style>
